import type { ApolloError } from '@apollo/client';

/**
 * Business error codes returned by the IELTS attempt mutations.
 *
 * They arrive over **two channels**, and a single mutation can use either:
 *
 * - `payload.userErrors[]` — errors the learner can act on. HTTP 200, no
 *   `errors[]`, and `data` is populated, so Apollo's `onError` never fires.
 *   See `USER_ERROR_CHANNEL_CODES`.
 * - `errors[].extensions.code` — session and system failures. `data` is null.
 *
 * `message` on either channel is debugging detail and is never shown to a learner.
 *
 * Historical note: the start/end mutations used to answer with Go struct names
 * (`ClientError`, `NotFoundError`) in `extensions.code`; those no longer appear.
 */
export const IeltsAttemptErrorCode = {
  NotAuthenticated: 'NOT_AUTHENTICATED',
  LearnerProfileMismatch: 'LEARNER_PROFILE_MISMATCH',
  AlreadySubmitted: 'ALREADY_SUBMITTED',
  OutOfTime: 'OUT_OF_TIME',
  SectionExpired: 'SECTION_EXPIRED',
  NotEnoughDiamond: 'NOT_ENOUGH_DIAMOND',
  NotFound: 'NOT_FOUND',
  ExamConfigError: 'EXAM_CONFIG_ERROR',
  CheckpointNotConfigured: 'CHECKPOINT_NOT_CONFIGURED',
  AttemptMalformed: 'ATTEMPT_MALFORMED',
  StructureNotFound: 'STRUCTURE_NOT_FOUND',
  StructureNotConfigured: 'STRUCTURE_NOT_CONFIGURED',
  ReachLimit: 'REACH_LIMIT',
  UserNotFound: 'USER_NOT_FOUND',
  InternalInconsistency: 'INTERNAL_INCONSISTENCY',
  InternalServerError: 'INTERNAL_SERVER_ERROR',
} as const;

/**
 * Codes the backend moves into `payload.userErrors[]`. Listed for documentation
 * and for `resolveUserError`, which does not filter on it — any code found in
 * `userErrors` is resolved through the same table, so a code the backend
 * reroutes later keeps working without a change here.
 */
export const USER_ERROR_CHANNEL_CODES: readonly string[] = [
  IeltsAttemptErrorCode.NotEnoughDiamond,
  IeltsAttemptErrorCode.OutOfTime,
  IeltsAttemptErrorCode.AlreadySubmitted,
  IeltsAttemptErrorCode.LearnerProfileMismatch,
  IeltsAttemptErrorCode.SectionExpired,
  IeltsAttemptErrorCode.ReachLimit,
];

export type IeltsAttemptErrorCodeValue =
  (typeof IeltsAttemptErrorCode)[keyof typeof IeltsAttemptErrorCode];

/** Which mutation raised the error — it changes how intrusive the UI may be. */
export type AttemptErrorPhase = 'start' | 'submit' | 'end';

/** The single way out we offer the learner. */
export type AttemptErrorAction =
  | 'login'
  | 'result'
  | 'diamond'
  | 'back'
  | 'restart'
  | 'retry'
  | 'none';

export interface ResolvedAttemptError {
  /** `modal` blocks the learner; `toast` is transient. */
  kind: 'toast' | 'modal';
  /** The raw code, or `UNKNOWN` for network errors and codes not in the table. */
  code: string;
  /** Suffix under the `ielts:error.attempt.*` namespace. */
  i18nKey: string;
  action: AttemptErrorAction;
  /** Backend/admin bug rather than learner error — worth a Sentry report. */
  report: boolean;
}

const UNKNOWN_CODE = 'UNKNOWN';

type ErrorPresentation = Pick<ResolvedAttemptError, 'i18nKey' | 'action' | 'report'>;

const PRESENTATION_BY_CODE: Record<string, ErrorPresentation> = {
  [IeltsAttemptErrorCode.NotAuthenticated]: {
    i18nKey: 'not_authenticated',
    action: 'login',
    report: false,
  },
  [IeltsAttemptErrorCode.UserNotFound]: {
    i18nKey: 'not_authenticated',
    action: 'login',
    report: true,
  },
  [IeltsAttemptErrorCode.LearnerProfileMismatch]: {
    i18nKey: 'profile_mismatch',
    action: 'login',
    report: false,
  },
  [IeltsAttemptErrorCode.AlreadySubmitted]: {
    i18nKey: 'already_submitted',
    action: 'result',
    report: false,
  },
  [IeltsAttemptErrorCode.OutOfTime]: {
    i18nKey: 'out_of_time',
    action: 'result',
    report: false,
  },
  [IeltsAttemptErrorCode.SectionExpired]: {
    i18nKey: 'out_of_time',
    action: 'result',
    report: false,
  },
  [IeltsAttemptErrorCode.NotEnoughDiamond]: {
    i18nKey: 'not_enough_diamond',
    action: 'diamond',
    report: false,
  },
  [IeltsAttemptErrorCode.NotFound]: {
    i18nKey: 'not_found',
    action: 'back',
    report: false,
  },
  // EXAM_CONFIG_ERROR replaces the three codes below; they are kept so the app
  // works both before and after that backend change.
  [IeltsAttemptErrorCode.ExamConfigError]: {
    i18nKey: 'checkpoint_broken',
    action: 'back',
    report: true,
  },
  [IeltsAttemptErrorCode.CheckpointNotConfigured]: {
    i18nKey: 'checkpoint_broken',
    action: 'back',
    report: true,
  },
  [IeltsAttemptErrorCode.StructureNotFound]: {
    i18nKey: 'checkpoint_broken',
    action: 'back',
    report: true,
  },
  [IeltsAttemptErrorCode.StructureNotConfigured]: {
    i18nKey: 'checkpoint_broken',
    action: 'back',
    report: true,
  },
  [IeltsAttemptErrorCode.AttemptMalformed]: {
    i18nKey: 'attempt_malformed',
    action: 'restart',
    report: true,
  },
  [IeltsAttemptErrorCode.ReachLimit]: {
    i18nKey: 'reach_limit',
    action: 'back',
    report: false,
  },
  [IeltsAttemptErrorCode.InternalInconsistency]: {
    i18nKey: 'internal',
    action: 'retry',
    report: true,
  },
  [IeltsAttemptErrorCode.InternalServerError]: {
    i18nKey: 'internal',
    action: 'retry',
    report: true,
  },
};

const FALLBACK_PRESENTATION: ErrorPresentation = {
  i18nKey: 'internal',
  action: 'retry',
  report: true,
};

/**
 * Codes that leave the learner unable to carry on, so they get a blocking modal
 * even mid-test where we otherwise stay out of the way.
 */
const BLOCKING_CODES = new Set<string>([
  IeltsAttemptErrorCode.AlreadySubmitted,
  IeltsAttemptErrorCode.OutOfTime,
  IeltsAttemptErrorCode.SectionExpired,
  IeltsAttemptErrorCode.LearnerProfileMismatch,
]);

/** Codes that make the attempt itself unusable, so ending it cannot be retried. */
const UNRECOVERABLE_END_CODES = new Set<string>([
  IeltsAttemptErrorCode.NotFound,
  IeltsAttemptErrorCode.AttemptMalformed,
]);

/**
 * Reads the business error code out of an Apollo error. Returns undefined for
 * network errors and for anything that is not a GraphQL error.
 */
export function getAttemptErrorCode(error: unknown): string | undefined {
  const graphQLErrors = (error as ApolloError | undefined)?.graphQLErrors;
  const code = graphQLErrors?.[0]?.extensions?.code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * A `UserError` from a mutation payload, typed loosely on purpose: the generated
 * `UserErrorCode` enum does not yet carry the codes the backend is moving to this
 * channel, so comparing against the enum would not type-check.
 */
export interface AttemptUserError {
  code: string;
  message?: string | null;
}

/**
 * Resolves the first entry of a mutation payload's `userErrors`, or undefined
 * when there is nothing wrong.
 *
 * This is the second error channel: the request succeeded at the transport level
 * (HTTP 200, `data` populated, no `errors[]`), so Apollo's `onError` does not
 * fire and the caller has to check the payload itself before treating the
 * mutation as successful.
 */
export function resolveUserError(
  userErrors: readonly AttemptUserError[] | null | undefined,
  phase: AttemptErrorPhase,
): ResolvedAttemptError | undefined {
  const first = userErrors?.[0];
  if (!first?.code) {
    return undefined;
  }

  // Reuse the extensions.code path so both channels map to the same behaviour.
  return resolveAttemptError(
    { graphQLErrors: [{ extensions: { code: first.code } }] } as unknown as ApolloError,
    phase,
  );
}

/**
 * Maps an Apollo error to the message and escape hatch we show the learner.
 *
 * The phase decides how intrusive we are: `start` always blocks (nothing is at
 * stake yet), `submit` runs while the learner is mid-answer so only genuinely
 * blocking codes interrupt them, and `end` must always leave a way out — a
 * learner stuck on the test screen with no working submit is the worst outcome.
 */
export function resolveAttemptError(
  error: unknown,
  phase: AttemptErrorPhase,
): ResolvedAttemptError {
  const code = getAttemptErrorCode(error);
  const presentation = (code && PRESENTATION_BY_CODE[code]) || FALLBACK_PRESENTATION;
  const resolvedCode = code && PRESENTATION_BY_CODE[code] ? code : UNKNOWN_CODE;

  let kind: ResolvedAttemptError['kind'] = 'modal';

  if (phase === 'submit') {
    kind = BLOCKING_CODES.has(resolvedCode) ? 'modal' : 'toast';
  } else if (phase === 'end') {
    kind =
      UNRECOVERABLE_END_CODES.has(resolvedCode) || BLOCKING_CODES.has(resolvedCode)
        ? 'modal'
        : 'toast';
  } else if (presentation.action === 'retry') {
    // A transient server blip on start is worth a retry, not a dead-end modal.
    kind = 'toast';
  }

  return {
    kind,
    code: resolvedCode,
    i18nKey: presentation.i18nKey,
    action: presentation.action,
    report: presentation.report,
  };
}
